// Brand-scheme theme registry and runtime resolver.
// Supports multiple client-specific brand schemes (spec R31).
//
// Two access paths exist and are deliberately kept separate:
//  - `theme` is the DEPLOYMENT-DEFAULT scheme, resolved once at module load from
//    the build-time env var (getThemeName). Existing consumers (the outer
//    CssVarsProvider in App.tsx and OrgThemeProvider's base theme) use this and
//    are byte-identical to before this change for the default deployment.
//  - `resolveBrandScheme(name)` resolves a scheme object by name at RUNTIME, so
//    a per-org scheme signal read after authentication can swap the base brand
//    palette (MetalMind orange #f59e0b vs SSI ocean blue #009ccf) for the same
//    build. OrgThemeProvider is the runtime consumer of this path.

import { defaultTheme } from "./defaultTheme";
import { ssiTheme } from "./ssiTheme";
import { getThemeName } from "../../config/runtimeConfig";

// Named brand-scheme registry - add new client schemes here.
export const themesRegistry = {
  default: defaultTheme,
  ssi: ssiTheme,
} as const;

export type BrandSchemeName = keyof typeof themesRegistry;

/** True when `name` is a registered brand-scheme key. */
export function isBrandSchemeName(name: unknown): name is BrandSchemeName {
  return typeof name === "string" && name in themesRegistry;
}

// The build-time deployment default scheme name (env-driven). Falls back to
// "default" for an unknown/empty env value so the export below is never
// undefined.
const deploymentName = getThemeName();
export const deploymentSchemeName: BrandSchemeName = isBrandSchemeName(
  deploymentName,
)
  ? deploymentName
  : "default";

/**
 * Resolve a brand-scheme theme object by name at RUNTIME. Unknown, empty, or
 * missing names fall back to the deployment default, so a per-org scheme signal
 * can be read defensively without any risk of an undefined theme.
 */
export function resolveBrandScheme(
  name?: string | null,
): (typeof themesRegistry)[BrandSchemeName] {
  return isBrandSchemeName(name)
    ? themesRegistry[name]
    : themesRegistry[deploymentSchemeName];
}

// Back-compat: the deployment-default theme, resolved once at module load. Per-
// org runtime selection is layered on top by OrgThemeProvider via
// resolveBrandScheme(); this export keeps the build-time scheme for callers that
// do not participate in the per-org swap.
export const theme = resolveBrandScheme(deploymentSchemeName);
